import { FC, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import toast from 'react-hot-toast';

import { useAuth } from '@hooks/Auth';

import InputText from '@components/elements/InputText';
import ButtonMighty from '@components/elements/ButtonMighty';
import Loader from '@components/elements/Loader';

import './SignupPage.css';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import TermsAndConditions from '@components/modules/TermsAndConditions';
import Checkbox from '@components/elements/Checkbox';
import Card from '@components/elements/Card';

const FORM_SCHEMA = z
  .object({
    username: z
      .string()
      .nonempty()
      .regex(/^[A-Za-z0-9]+$/, 'Username contains invalid characters.'),
    email: z.string().nonempty().email(),
    password: z.string().min(8).max(99),
    confirmPassword: z.string(),
    policy: z.boolean(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'], // set path of error
  });

type FormInput = z.infer<typeof FORM_SCHEMA>;

const SignupPage: FC = () => {
  const history = useHistory<any>();

  const { signUp } = useAuth();

  // useEffect(() => {f
  //   if (user) history.push('/login');
  // }, [user, history]);

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<FormInput>({
    mode: 'onChange',
    resolver: zodResolver(FORM_SCHEMA),
    shouldUnregister: false,
  });

  console.log({ errors });

  const onSubmit = useCallback(
    async (data: FormInput) => {
      if (!data.policy) {
        return toast.error(
          'You must agree to our Terms and Conditions to use MegaX World.'
        );
      }
      try {
        const authPayload = {
          username: data.username.trim(),
          password: data.password,
          attributes: {
            email: data.email,
            // phone_number: inputForm.phone.value,
          },
        };

        console.log('calling signUp');

        const result = await signUp(authPayload);
        console.log('success singup', result);

        if (result?.userConfirmed) {
          toast.success('Successfully created your account!');
          history.replace('/login');
        } else {
          toast.success(
            'Successfully created your account, verify your email.'
          );
          history.replace(
            `/verify${data.username ? `?username=${data.username}` : ''}`
          );
        }
      } catch (err: any) {
        console.error('signup err', err);
        console.log(err.message);
        if (err.code === 'UserNotConfirmedException') {
          toast.success(
            'Successfully created your account, verify your email.'
          );
          history.replace(
            `/verify${data.username ? `?username=${data.username}` : ''}`
          );
        } else if (err.message?.includes?.('EmailExistsException')) {
          toast.error('Email already in use.');
          setError('email', {
            type: 'manual',
            message: 'Email already in use.',
          });
        } else if (err.message === 'User already exists') {
          toast.error('Username already in use.');
          setError('username', {
            type: 'manual',
            message: 'Username already in use.',
          });
        } else {
          toast.error(err.message);
        }
      }
    },
    [history, setError, signUp]
  );

  const content = useMemo(() => {
    if (isSubmitting)
      return (
        <Loader
          title="Creating your account..."
          description="This may take a few seconds, please don't close this page."
        />
      );
    return (
      <form className="signup-form" onSubmit={handleSubmit(onSubmit)}>
        <div>
          <InputText
            type="text"
            placeholder="Username"
            {...register('username')}
          />
          {errors.username && (
            <p className="text-right text-red">*{errors.username.message}</p>
          )}
        </div>

        <div>
          <InputText
            type="email"
            placeholder="Your Email"
            {...register('email')}
          />
          {errors.email && (
            <p className="text-right text-red">*{errors.email.message}</p>
          )}
        </div>

        <div>
          <InputText
            type="password"
            placeholder="Password"
            {...register('password')}
          />
          {errors.password && (
            <p className="text-right text-red">*{errors.password.message}</p>
          )}
        </div>

        <div>
          <InputText
            type="password"
            placeholder="Confirm Password"
            {...register('confirmPassword')}
          />
          {errors.confirmPassword && (
            <p className="text-right text-red">
              *{errors.confirmPassword.message}
            </p>
          )}
        </div>

        <Card bgColor="blue" shadowColor="salmon">
          <div className="flex flex-row items-center p-3 space-x-4">
            <Checkbox {...register('policy')} />
            <div>Agree to <a className='underline' target='_blank' rel='noreferrer'  href={'https://megax.world/megaxpoll-app-terms-conditions/'}>Terms and Conditions</a></div>
            {/* <div className="flex flex-col items-center">
              <TermsAndConditions id="63267831" type="terms-and-conditions">
                Terms and Conditions
              </TermsAndConditions>
            </div> */}
          </div>
        </Card>
        <div className="signup-action-container">
          <ButtonMighty
            type="submit"
            disabled={isSubmitting}
            loading={isSubmitting}
            color="#4400FF"
            text="Sign up"
            borderColor="#ff1dfc"
            margin="0"
            textColor="#fff"
          />
        </div>
      </form>
    );
  }, [
    isSubmitting,
    handleSubmit,
    onSubmit,
    register,
    errors.username,
    errors.email,
    errors.password,
    errors.confirmPassword,
  ]);
  return (
    <div className="pt-20 signup-page">
      <div className="container mx-auto md:w-108">
        <div className="px-4 py-4 mx-4 bg-yellow lg:px-8 lg:mx-0">
          <h1 className="mt-8 mb-8 text-6xl font-bold">Sign Up</h1>
          <div>{content}</div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
